import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Authenticator } from '@aws-amplify/ui-react';
import { WeddingProvider, useWeddingContext } from '../context/WeddingContext';
import { Amplify } from 'aws-amplify';
import awsConfig from '../config/aws-config';

// Amplify設定
const amplifyConfig = {
  aws_project_region: awsConfig.cognito.region,
  aws_user_pools_id: awsConfig.cognito.userPoolId,
  aws_user_pools_web_client_id: awsConfig.cognito.userPoolWebClientId
};

Amplify.configure(amplifyConfig);

const InvitePageContent: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { joinByInviteToken } = useWeddingContext();
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [familyName, setFamilyName] = useState<string>('');
  const [joinStatus, setJoinStatus] = useState<'pending' | 'joining' | 'success' | 'error' | 'already_member'>('pending');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 2;

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      setInviteToken(token);
    } else {
      navigate('/');
    }
  }, [searchParams, navigate]);

  // リトライ機能
  const handleRetry = useCallback(() => {
    // 既にメンバーの場合は再試行しない
    if (joinStatus === 'already_member') {
      return;
    }
    
    if (retryCount < maxRetries) {
      setRetryCount(prev => prev + 1);
      setJoinStatus('pending');
      setErrorMessage('');
    }
  }, [retryCount, maxRetries, joinStatus]);

  // 認証完了後に自動的に参加処理を実行
  useEffect(() => {
    const handleAutoJoin = async () => {
      if (inviteToken && (joinStatus === 'pending' || retryCount > 0)) {
        setJoinStatus('joining');
        setErrorMessage('');
        
        // タイムアウト処理（15秒）
        const timeoutId = setTimeout(() => {
          setJoinStatus('error');
          setErrorMessage('参加処理がタイムアウトしました。ネットワーク接続を確認してください。');
        }, 15000);

        try {
          const result = await joinByInviteToken(inviteToken);
          clearTimeout(timeoutId);
          
          if (result?.weddingGroup) {
            setFamilyName(result.weddingGroup.familyName || 'ファミリーアカウント');
            setJoinStatus('success');
            // 3秒後にダッシュボードへリダイレクト
            setTimeout(() => {
              navigate('/');
            }, 3000);
          } else {
            setJoinStatus('error');
            setErrorMessage('参加処理は完了しましたが、グループ情報の取得に失敗しました。');
          }
        } catch (error) {
          clearTimeout(timeoutId);
          console.error('Auto join failed:', error);
          
          // エラーの詳細に基づいて適切なメッセージを設定
          let specificErrorMessage = '';
          let isAlreadyMember = false;
          let canRetry = true;
          let statusCode = null;
          
          // Fetch エラーからレスポンスボディを取得
          if (error instanceof Error) {
            try {
              // エラーメッセージからHTTPステータスコードを抽出
              const statusMatch = error.message.match(/HTTP (\d{3})/);
              if (statusMatch) {
                statusCode = parseInt(statusMatch[1]);
              }
              
              // JSONエラーレスポンスを解析
              const errorBodyMatch = error.message.match(/Response body: (.+)$/);
              if (errorBodyMatch) {
                const errorBody = JSON.parse(errorBodyMatch[1]);
                if (errorBody.error) {
                  specificErrorMessage = errorBody.error;
                  
                  // エラーコードに基づく処理
                  switch (errorBody.code) {
                    case 'ALREADY_MEMBER':
                      isAlreadyMember = true;
                      break;
                    case 'SERVICE_UNAVAILABLE':
                      specificErrorMessage = 'サーバーが混雑しています。しばらく待ってから再度お試しください。';
                      canRetry = true;
                      break;
                    case 'INVALID_INVITE_TOKEN':
                      specificErrorMessage = '招待リンクが無効または期限切れです。新しい招待リンクを取得してください。';
                      canRetry = false;
                      break;
                    case 'USER_NOT_FOUND':
                      specificErrorMessage = 'アカウントが見つかりません。ログアウトして再度ログインしてください。';
                      canRetry = false;
                      break;
                    case 'GROUP_NOT_FOUND':
                      specificErrorMessage = '招待されたファミリーグループが見つかりません。';
                      canRetry = false;
                      break;
                  }
                }
              }
            } catch (parseError) {
              console.error('Error parsing error response:', parseError);
            }
            
            // 特定のエラーメッセージがない場合のフォールバック処理
            if (!specificErrorMessage) {
              const errorMessage = error.message.toLowerCase();
              
            if (errorMessage.includes('already a member') ||
                errorMessage.includes('既にメンバー') ||
                  errorMessage.includes('already exists')) {
              isAlreadyMember = true;
            } else if (errorMessage.includes('invalid') || errorMessage.includes('無効')) {
                specificErrorMessage = '招待リンクが無効または期限切れです。新しい招待リンクを取得してください。';
                canRetry = false;
            } else if (errorMessage.includes('not found') || errorMessage.includes('見つかりません')) {
                specificErrorMessage = '招待されたファミリーアカウントが見つかりません。';
                canRetry = false;
              } else if (errorMessage.includes('503') || errorMessage.includes('service unavailable')) {
                specificErrorMessage = 'サーバーが混雑しています。しばらく待ってから再度お試しください。';
                canRetry = true;
              } else if (statusCode === 503) {
                specificErrorMessage = 'サーバーが混雑しています。しばらく待ってから再度お試しください。';
                canRetry = true;
            } else {
              specificErrorMessage = error.message;
              }
            }
          } else if (typeof error === 'string') {
            const errorMessage = error.toLowerCase();
            if (errorMessage.includes('already a member') ||
                errorMessage.includes('既にメンバー')) {
              isAlreadyMember = true;
            } else {
              specificErrorMessage = error;
            }
          } else {
            specificErrorMessage = '参加処理中にエラーが発生しました。ネットワーク接続を確認して再度お試しください。';
          }
          
          // 既にメンバーの場合
          if (isAlreadyMember) {
            setJoinStatus('already_member');
            setTimeout(() => {
              navigate('/');
            }, 3000);
            return;
          }
          
          // リトライできない致命的エラーの場合
          if (!canRetry || retryCount >= maxRetries) {
            setErrorMessage(specificErrorMessage);
            setJoinStatus('error');
            return;
          }
          
          // リトライ可能なエラーの場合は自動的にリトライ
          if (retryCount < maxRetries) {
            console.log(`Retrying auto join... (${retryCount + 1}/${maxRetries})`);
            setTimeout(() => {
              setRetryCount(prev => prev + 1);
            }, 2000 * (retryCount + 1)); // 段階的に遅延を増加
          } else {
          setErrorMessage(specificErrorMessage);
          setJoinStatus('error');
          }
        }
      }
    };

    handleAutoJoin();
  }, [inviteToken, joinByInviteToken, joinStatus, navigate, retryCount]);

  const renderContent = () => {
    switch (joinStatus) {
      case 'joining':
        return (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">ファミリーアカウントに参加中...</h2>
            <p className="text-gray-600">しばらくお待ちください</p>
            {retryCount > 0 && (
              <p className="text-sm text-gray-500 mt-2">再試行中... ({retryCount}/{maxRetries})</p>
            )}
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="text-green-500 text-6xl mb-4">✅</div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">参加完了！</h2>
            <p className="text-gray-600 mb-4">
              {familyName ? `${familyName}` : 'ファミリーアカウント'}への参加が完了しました
            </p>
            <p className="text-sm text-gray-500">自動的にダッシュボードに移動します...</p>
          </div>
        );

      case 'error':
        return (
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">❌</div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">参加に失敗しました</h2>
            <p className="text-gray-600 mb-4">{errorMessage}</p>
            <div className="flex gap-2 justify-center">
              {retryCount < maxRetries && (
                <button
                  onClick={handleRetry}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                >
                  再試行 ({retryCount + 1}/{maxRetries + 1})
                </button>
              )}
              <button
                onClick={() => navigate('/')}
                className="bg-pink-600 hover:bg-pink-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
              >
                ホームに戻る
              </button>
            </div>
          </div>
        );

      case 'already_member':
        return (
          <div className="text-center">
            <div className="text-green-500 text-6xl mb-4">✅</div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">既に参加済みです</h2>
            <p className="text-gray-600 mb-4">
              {familyName ? `${familyName}` : 'ファミリーアカウント'}に既に参加しています
            </p>
            <p className="text-sm text-gray-500">自動的にダッシュボードに移動します...</p>
          </div>
        );

      default:
        return (
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">ファミリーアカウントへの招待</h2>
            <p className="text-gray-600 mb-6">
              ログインまたはアカウントを作成して、ファミリーアカウントに参加してください
            </p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-pink-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-pink-600 mb-2">🎊 Wedding Task Planner</h1>
        </div>
        
        {renderContent()}
      </div>
    </div>
  );
};

const InvitePage: React.FC = () => {
  return (
    <Authenticator
      signUpAttributes={[]}
      variation="modal"
      components={{
        Header: () => (
          <div className="text-center p-6">
            <h1 className="text-3xl font-bold text-pink-600 mb-2">🎊 Wedding Task Planner</h1>
            <p className="text-gray-600">ファミリーアカウントに参加</p>
            <p className="text-xs text-gray-500 mt-1">メールアドレスがログイン用のユーザー名として使用されます</p>
          </div>
        ),
        SignUp: {
          Header: () => (
            <div className="text-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">アカウント作成</h2>
              <p className="text-sm text-gray-600 mb-2">
                ファミリーアカウントに参加するためのアカウントを作成します
              </p>
              <div className="bg-pink-50 border border-pink-200 rounded-lg p-3">
                <p className="text-xs text-pink-700">
                  💡 メールアドレス1つでアカウント作成できます（ユーザー名として使用されます）
                </p>
              </div>
            </div>
          )
        },
        SignIn: {
          Header: () => (
            <div className="text-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">ログイン</h2>
              <p className="text-sm text-gray-600 mb-2">
                既存のアカウントでファミリーアカウントに参加
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-700">
                  📧 登録済みのメールアドレスを入力してください
                </p>
              </div>
          </div>
        )
        }
      }}
    >
      {({ signOut, user }) => (
        <WeddingProvider user={user}>
          <InvitePageContent />
        </WeddingProvider>
      )}
    </Authenticator>
  );
};

export default InvitePage; 